import { useState, useEffect, useCallback } from 'react';
import { workflowService } from '../services/workflow';
import toast from 'react-hot-toast';

interface ValidationStep {
  step_order: number;
  step_label: string | null;
  can_resolve_here: boolean;
  validator: { id: number; name: string } | null;
  validator_role: string | null;
  status: string;
  feedback: string | null;
  action_at: string | null;
}

interface WorkflowSummaryData {
  workflow_config: {
    id: number;
    name: string;
    source_unit_type: string;
    max_resolution_level: string;
    step_count: number;
  } | null;
  current_step: number | null;
  total_steps: number;
  completed: number;
  can_approve: boolean;
  can_reject: boolean;
  can_resolve: boolean;
  resolution: string | null;
  needs_rapim: boolean | null;
  steps: ValidationStep[];
}

export function useWorkflowValidation(laporanId: number | null) {
  const [summary, setSummary] = useState<WorkflowSummaryData | null>(null);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);

  const fetchSummary = useCallback(async () => {
    if (!laporanId) return;
    setLoading(true);
    try {
      const data = await workflowService.getSummary(laporanId);
      setSummary(data);
    } catch (err) {
      console.error('[WorkflowValidation] Failed to load summary:', err);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [laporanId]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  // ✅ FIX: currentStep hanya dari backend
  // Filter step yang benar-benar pending DAN sesuai current_validation_step dari backend
  const currentStep = summary?.current_step != null
    ? summary?.steps?.find(
        (s) => s.step_order === summary.current_step && s.status === 'pending'
      ) || null
    : null;

  // ✅ Tambahkan: apakah ada step pending aktif (untuk validasi tombol aksi)
  const hasActivePendingStep = summary?.steps?.some((s) => s.status === 'pending') ?? false;

  const canApprove = summary?.can_approve ?? false;
  const canReject = summary?.can_reject ?? false;
  const canResolve = summary?.can_resolve ?? false;

  const processAction = useCallback(
    async (action: 'approve' | 'reject' | 'resolve', feedback?: string): Promise<boolean> => {
      if (!laporanId) return false;
      setProcessing(true);
      try {
        await workflowService.processValidation(laporanId, action, feedback);
        const messages = {
          approve: 'Laporan berhasil disetujui.',
          reject: 'Laporan ditolak dan dikembalikan ke pelapor.',
          resolve: 'Laporan diselesaikan di level Anda.',
        };
        toast.success(messages[action]);
        await fetchSummary();
        return true;
      } catch (err: any) {
        const msg = err?.response?.data?.message || 'Gagal memproses validasi.';
        toast.error(msg);
        return false;
      } finally {
        setProcessing(false);
      }
    },
    [laporanId, fetchSummary]
  );

  return {
    summary,
    loading,
    processing,
    currentStep,
    canApprove,
    canReject,
    canResolve,
    hasActivePendingStep,
    approve: (feedback?: string) => processAction('approve', feedback),
    reject: (feedback: string) => processAction('reject', feedback),
    resolve: (feedback: string) => processAction('resolve', feedback),
    refresh: fetchSummary,
  };
}
